'use client';
import { MouseEvent, useEffect, useRef, useState } from 'react';

import { useScreenSize } from '@/hooks/useScreenSize';
import { cn } from '@/lib/utils';

import useForceRerender from '@/hooks/useForceRerender';
import { gradientBorderStyling } from '@/styles/gradient';
import TrackerClickWrapper from '../TrackerClickWrapper';

type Size = 'xs' | 'md';
export interface TogglesProps {
  options: string[];
  selectedIndex?: number;
  onChange?: (event: MouseEvent<HTMLDivElement>, index: number) => void;
  onValueChange?: (index: number) => void;
  size?: Size;
  classNames?: {
    root?: string;
    tab?: string;
    selectedTab?: string;
  };
}

const mutualStyles = {
  root: 'md:border-neutral-N04 md:bg-neutral-N00 md:hover:bg-neutral-N04 bg-neutral-N00 relative flex w-full flex-col gap-0.5 border-none bg-transparent md:min-w-[auto] md:flex-row md:border md:border-solid',
  tab: 'border-neutral-N04 relative z-[1] w-full cursor-pointer border border-solid text-center md:static md:border-none',
  selectedTab:
    'bg-neutral-N99 absolute top-0.5 hidden transform transition duration-300 md:block',
};

const sizeMap: { [K in Size]: typeof mutualStyles } = {
  md: {
    root: 'gap-y-5 rounded-3xl md:w-fit',
    tab: 'rounded-3xl px-6 py-2.5 md:w-[160px] md:min-w-fit',
    selectedTab: 'min-w-[160px] rounded-[24px] p-3',
  },
  xs: {
    root: 'gap-y-1 rounded-md text-body-s',
    tab: 'rounded-md px-3 py-1 md:w-fit md:flex-1',
    selectedTab: 'min-w-[30px] rounded-md p-1',
  },
};

export const Toggles = ({
  options,
  selectedIndex: controlledIndex,
  onChange,
  onValueChange,
  classNames,
  size = 'md',
}: TogglesProps) => {
  const [uncontrolledIndex, setUncontrolledIndex] = useState(0);
  const isControlled = controlledIndex !== undefined;
  const selectedIndex = isControlled ? controlledIndex : uncontrolledIndex;

  const tabsRef = useRef<Map<number, HTMLDivElement>>(new Map());

  const borderRadius = size === 'md' ? 'rounded-3xl' : 'rounded-md';

  useScreenSize();

  useEffect(() => {
    if (!isControlled && controlledIndex !== undefined) {
      setUncontrolledIndex(controlledIndex);
    }
  }, [controlledIndex, isControlled]);

  const getActiveItemDimensions = (element: HTMLElement) => ({
    x: Math.max(2, element.offsetLeft - 2),
    width: element.offsetWidth,
  });

  const selectedTabEl = tabsRef.current.get(selectedIndex);
  const selectedTabDimensions = selectedTabEl
    ? getActiveItemDimensions(selectedTabEl)
    : {
        x: 0,
        width: 0,
      };

  function getStylingColorsForTab(selectedIndex: number, index: number) {
    if (selectedIndex === index) {
      return cn(
        'bg-neutral-N99 md:bg-transparent text-neutral-N00 border-none md:dark:text-neutral-dark-N00 dark:text-neutral-dark-N00 dark:bg-neutral-dark-N99 dark:bg-transparent',
        gradientBorderStyling({
          borderWidth: 'p-0.5',
          borderRadius: borderRadius,
          utilities: 'max-md:dark:',
        })
      );
    }

    if (selectedIndex !== index) {
      return 'text-neutral-N99 md:bg-transparent bg-neutral-N00';
    }
  }

  function handleChange(e: MouseEvent<HTMLDivElement>, index: number) {
    if (options.length === 2 && selectedIndex === index) {
      index = index === 0 ? 1 : 0;
    }

    if (!isControlled) {
      onValueChange?.(index);
      setUncontrolledIndex(index);
    }

    if (onChange) {
      onChange(e, index);
    }
  }
  // as is often the case with refs, we need to force a rerender to set the correct dimensions
  useForceRerender(!selectedTabDimensions.width);

  const styles = sizeMap[size];

  return (
    <div className={cn(mutualStyles.root, styles.root, classNames?.root)}>
      {options.map((option, index) => (
        <div
          ref={(node) => {
            const map = tabsRef.current;
            if (node) {
              map.set(index, node);
            }
          }}
          className={cn(
            mutualStyles.tab,
            styles.tab,
            getStylingColorsForTab(selectedIndex, index),
            classNames?.tab
          )}
          key={index}
          data-tracker-context={`toggle-index-${index}`}
          onClick={(event) => handleChange(event, index)}
        >
          <TrackerClickWrapper>{option}</TrackerClickWrapper>
        </div>
      ))}
      <div
        style={{
          width: selectedTabDimensions.width,
          transform: `translateX(${selectedTabDimensions.x}px)`,
          height: 'calc(100% - 4px)',
        }}
        className={cn(
          mutualStyles.selectedTab,
          styles.selectedTab,
          `dark:bg-neutral-N00 dark:before:md:border-transparent ${gradientBorderStyling(
            {
              borderWidth: 'p-0.5',
              borderRadius: borderRadius,
              utilities: 'dark:',
            }
          )}`,
          classNames?.selectedTab
        )}
      />
    </div>
  );
};
